// Offline phase 2 (PMU-271): the door tablet as the hub on the school's Wi-Fi. When the internet is
// down, staff phones and the door still reach each other: a door request reaches the staff, their
// answer reaches the door, and a handover recorded on any device shows on the others. Every device
// still sends its own records to the cloud when the internet returns; the hub only passes them on.
//
// Trust. Both ends prove they are approved devices of the same school: each signs its side of the
// handshake with its device key, checked against the school's device list (GET /device/peers,
// kept on the device). The handshake also agrees a fresh key (ECDH P-256, ephemeral, HKDF-SHA-256)
// for AES-GCM, so nothing crosses the Wi-Fi in the clear, and a recorded session cannot be replayed
// into a new one (the hub's nonce is in both signatures). Records passed on are checked against
// the device that signed them, so a phone cannot pass on a record as another device's.
//
// The native side only moves text frames (door: a WebSocket server announced by NSD; phone: NSD
// discovery and a WebSocket client); everything here is plain code, tested on the laptop.
import { b64url, fromB64url, signCanonical, verifyCanonical, type PreImage } from "@vavr/core";
import type { DeviceApi } from "./api.ts";
import type { VavrDb } from "./db.ts";

export const LAN_SERVICE = "_vavr._tcp";
const PROTO = "vavr-lan-1";
/**
 * Both ends ping every LAN_PING_MS ({"t":"p"}, no data) and close a link silent for LAN_SILENT_MS:
 * a door that restarts across some Wi-Fi networks leaves the phone's socket half-open, unnoticed.
 */
export const LAN_PING_MS = 15_000;
export const LAN_SILENT_MS = 45_000;
const PING = JSON.stringify({ t: "p" });
/** Only the day's records travel: a door left on overnight does not hand yesterday's requests out. */
export const LAN_MAX_AGE_MS = 16 * 3600_000;
const fresh = (at: number, now = Date.now()) => now - at < LAN_MAX_AGE_MS;
/** The day's records and answers, without anything older than LAN_MAX_AGE_MS. */
export function lanToday<T extends { events: LanEvent[]; answers: LanAnswer[] }>(s: T, now = Date.now()): T {
  return { ...s, events: s.events.filter((e) => fresh(e.pre.device_ts, now)), answers: s.answers.filter((a) => fresh(a.at, now)) };
}

/** An approved device of the school, from GET /api/v1/device/peers. */
export interface Peer { id: string; kind: "kiosk" | "phone"; pubkey: string }

/** A signed record as it sits in a device's outbox. */
export interface LanEvent { pre: PreImage; device_sig: string }
/** A staff answer to a door request ("coming", "not yet"…), shown on the door at once. */
export interface LanAnswer { request_id: string; status: string; note: string | null; by: string; at: number; name?: string | null }

/**
 * Offline phase 3 (PMU-272): the door's radio relay on the Wi-Fi. The key (base64url, 32 bytes) is
 * new each time the door starts and goes only to phones that passed the handshake; `cloud` says
 * whether the door reaches the internet (if not, the school's radios move to the relay).
 */
export interface LanRadioOffer { key: string; port: number; cloud: boolean }

/** What travels inside the encrypted channel. */
export type LanMessage =
  | { k: "event"; ev: LanEvent }
  | { k: "answer"; answer: LanAnswer }
  | { k: "catchup"; events: LanEvent[]; answers: LanAnswer[] }
  | ({ k: "radio" } & LanRadioOffer);

/** What a phone sends: its records and answers (the door alone offers the radio and catch-ups). */
export type LanOutgoing = Extract<LanMessage, { k: "event" } | { k: "answer" }>;

/** Fetches the school's approved devices and keeps them for when the internet is down. */
export async function refreshPeers(db: VavrDb, api: DeviceApi): Promise<Peer[]> {
  const res = await api.request("GET", "/api/v1/device/peers");
  if (!res.ok) throw new Error(`peers ${res.status}`);
  const { peers } = (await res.json()) as { peers: Peer[] };
  await db.meta.put({ key: "peers", value: { peers, fetched_at: Date.now() } });
  return peers;
}
export async function readPeers(db: VavrDb): Promise<Peer[]> {
  const rec = await db.meta.get("peers");
  return ((rec?.value as { peers?: Peer[] } | undefined)?.peers) ?? [];
}

interface Me { siteId: string; deviceId: string; privateKey: CryptoKey; peers: () => Peer[] }

// ---------- crypto ----------

const ECDH = { name: "ECDH", namedCurve: "P-256" } as const;
async function ephemeral() {
  const k = await crypto.subtle.generateKey(ECDH, false, ["deriveBits"]);
  return { key: k.privateKey, pub: b64url(await crypto.subtle.exportKey("raw", k.publicKey)) };
}
async function channelKey(mine: CryptoKey, theirPub: string, nonce: string, doorPub: string, phonePub: string): Promise<CryptoKey> {
  const them = await crypto.subtle.importKey("raw", fromB64url(theirPub) as Uint8Array<ArrayBuffer>, ECDH, false, []);
  const shared = await crypto.subtle.deriveBits({ name: "ECDH", public: them }, mine, 256);
  const hk = await crypto.subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: fromB64url(nonce) as Uint8Array<ArrayBuffer>, info: new TextEncoder().encode(`${PROTO}|${doorPub}|${phonePub}`) },
    hk, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"],
  );
}
async function seal(key: CryptoKey, msg: LanMessage): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, new TextEncoder().encode(JSON.stringify(msg)));
  return JSON.stringify({ t: "x", iv: b64url(iv), ct: b64url(ct) });
}
async function open(key: CryptoKey, f: { iv: string; ct: string }): Promise<LanMessage> {
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: fromB64url(f.iv) as Uint8Array<ArrayBuffer> }, key, fromB64url(f.ct) as Uint8Array<ArrayBuffer>);
  return JSON.parse(new TextDecoder().decode(pt)) as LanMessage;
}
const peer = (me: Me, id: string, kind?: Peer["kind"]) => me.peers().find((p) => p.id === id && (!kind || p.kind === kind)) ?? null;

/** A record is passed on only if the device named in it signed it, for this school. */
export async function eventIsGenuine(me: Me, ev: LanEvent): Promise<boolean> {
  const p = ev?.pre?.site_id === me.siteId ? peer(me, ev.pre.device_id) : null;
  if (!p) return false;
  try { return await verifyCanonical(p.pubkey, ev.pre, ev.device_sig); } catch { return false; }
}

// ---------- the hub (the door tablet) ----------

interface Conn { id: string; nonce: string; eph: { key: CryptoKey; pub: string }; key: CryptoKey | null; device: string | null; heard: number }

export interface HubOptions extends Me {
  /** Sends a text frame to one connection (the native server). */
  send: (conn: string, text: string) => void;
  close: (conn: string) => void;
  /** A record or an answer from a phone, for the door's own screens. */
  onMessage?: (msg: LanMessage, from: string) => void;
  /** Kept for phones that join later: the day's records and answers, newest last. */
  keep?: number;
  /** What the door saved before it restarted (the day's records), to hand to phones that join. */
  initial?: { events: LanEvent[]; answers: LanAnswer[] };
  /** The day's records changed: the door saves them, so a restart does not lose them. */
  onStore?: (day: { events: LanEvent[]; answers: LanAnswer[] }) => void;
  /** Records now held by at least one staff phone (sent live or in a catch-up), by record id. */
  onDelivered?: (ids: string[]) => void;
  /** How many staff phones are linked now. */
  onPhones?: (n: number) => void;
  /** What the door's log keeps (uploaded later): links made, ended or refused, and what was relayed to whom. */
  onLog?: (kind: "link" | "relay", data: Record<string, unknown>) => void;
}

export class LanHub {
  private conns = new Map<string, Conn>();
  private events: LanEvent[] = [];
  private answers: LanAnswer[] = [];
  private radio: LanRadioOffer | null = null;
  private o: HubOptions;
  constructor(o: HubOptions) {
    this.o = o;
    if (o.initial) { const d = lanToday(o.initial); this.events = d.events; this.answers = d.answers; }
  }

  private prune() { const d = lanToday({ events: this.events, answers: this.answers }); this.events = d.events; this.answers = d.answers; }
  private phonesChanged() { this.o.onPhones?.(this.phones().length); }

  /** Connected and authenticated staff phones (another door linking here for the radio is not one). */
  phones() { return [...this.conns.values()].filter((c) => c.key && c.device && this.isPhone(c.device)).map((c) => c.device!); }
  private isPhone(device: string) { return peer(this.o, device)?.kind === "phone"; }

  async opened(conn: string) {
    const nonce = b64url(crypto.getRandomValues(new Uint8Array(16)));
    const eph = await ephemeral();
    this.conns.set(conn, { id: conn, nonce, eph, key: null, device: null, heard: Date.now() });
    const sig = await signCanonical(this.o.privateKey, { p: PROTO, t: "hi", site: this.o.siteId, door: this.o.deviceId, eph: eph.pub, n: nonce });
    this.o.send(conn, JSON.stringify({ t: "hi", site: this.o.siteId, door: this.o.deviceId, eph: eph.pub, n: nonce, sig }));
  }

  closed(conn: string) {
    const c = this.conns.get(conn);
    this.conns.delete(conn);
    if (c?.key) { this.o.onLog?.("link", { event: "unlinked", device: c.device }); this.phonesChanged(); }
  }

  async received(conn: string, text: string) {
    const c = this.conns.get(conn);
    if (!c) return;
    let f: Record<string, string>;
    try { f = JSON.parse(text); } catch { return this.drop(conn); }
    c.heard = Date.now();
    if (f.t === "p") return;
    if (!c.key) {
      // The phone's hello: an approved phone of this school, signing this session's nonce and keys.
      if (f.t !== "hello" || typeof f.device !== "string" || typeof f.eph !== "string") { this.o.onLog?.("link", { event: "refused", device: typeof f.device === "string" ? f.device.slice(0, 40) : null, reason: "bad_hello" }); return this.drop(conn); }
      const p = peer(this.o, f.device);
      const ok = p && f.device !== this.o.deviceId && await verifyCanonical(p.pubkey, { p: PROTO, t: "hello", site: this.o.siteId, device: f.device, eph: f.eph, door_eph: c.eph.pub, n: c.nonce }, f.sig!).catch(() => false);
      if (!ok) { this.o.onLog?.("link", { event: "refused", device: f.device.slice(0, 40), reason: p ? "bad_signature" : "not_on_list" }); return this.drop(conn); }
      c.key = await channelKey(c.eph.key, f.eph, c.nonce, c.eph.pub, f.eph);
      c.device = f.device;
      this.prune();
      this.o.send(conn, await seal(c.key, { k: "catchup", events: this.events, answers: this.answers }));
      if (this.radio) this.o.send(conn, await seal(c.key, { k: "radio", ...this.radio }));
      this.o.onLog?.("link", { event: "linked", device: f.device, peer_kind: p!.kind, caught_up: this.events.length });
      if (this.events.length && this.isPhone(f.device)) this.o.onDelivered?.(this.events.map((e) => e.pre.id));
      this.phonesChanged();
      return;
    }
    if (f.t !== "x") return;
    let msg: LanMessage;
    try { msg = await open(c.key, f as { iv: string; ct: string }); } catch { return this.drop(conn); }
    await this.accept(msg, c.device!);
  }

  /** The door's own records and answers go out the same way. */
  async publish(msg: LanOutgoing) { await this.accept(msg, this.o.deviceId); }

  /** The door's radio relay (or none), to every linked phone now and to each that joins. */
  async setRadio(offer: LanRadioOffer | null) {
    const same = offer && this.radio && offer.key === this.radio.key && offer.port === this.radio.port && offer.cloud === this.radio.cloud;
    if (same || (!offer && !this.radio)) return;
    this.radio = offer;
    if (!offer) return;
    for (const c of this.conns.values()) if (c.key) this.o.send(c.id, await seal(c.key, { k: "radio", ...offer }));
  }

  private async accept(msg: LanMessage, from: string) {
    this.prune();
    if (msg.k === "event") {
      const ev = msg.ev;
      if (!fresh(ev.pre?.device_ts) || !(await eventIsGenuine(this.o, ev))) return;
      if (this.events.some((e) => e.pre.id === ev.pre.id)) return;
      this.events.push(ev);
      this.events = this.events.slice(-(this.o.keep ?? 1000));
    } else if (msg.k === "answer") {
      if (!fresh(msg.answer?.at)) return;
      const a = { ...msg.answer, by: from === this.o.deviceId ? msg.answer.by : from };
      this.answers = [...this.answers.filter((x) => !(x.request_id === a.request_id && x.by === a.by)), a].slice(-(this.o.keep ?? 1000));
      msg = { k: "answer", answer: a };
    } else return;
    this.o.onStore?.({ events: this.events, answers: this.answers });
    if (from !== this.o.deviceId) this.o.onMessage?.(msg, from);
    let sent = 0;
    const to: string[] = [];
    for (const c of this.conns.values()) if (c.key && c.device !== from) { this.o.send(c.id, await seal(c.key, msg)); to.push(c.device!); if (this.isPhone(c.device!)) sent++; }
    if (sent && msg.k === "event") this.o.onDelivered?.([msg.ev.pre.id]);
    if (msg.k === "event") this.o.onLog?.("relay", { record: msg.ev.pre.id, type: msg.ev.pre.type, child_id: msg.ev.pre.child_id, from, to });
    else this.o.onLog?.("relay", { answer_for: msg.answer.request_id, status: msg.answer.status, by: msg.answer.by, from, to });
  }

  /** Every LAN_PING_MS: pings the linked devices, and drops one silent too long (and a handshake never finished). */
  tick(now = Date.now()) {
    for (const c of [...this.conns.values()]) {
      if (now - c.heard > LAN_SILENT_MS) { const was = !!c.key; this.drop(c.id); if (was) { this.o.onLog?.("link", { event: "silent", device: c.device }); this.phonesChanged(); } continue; }
      if (c.key) this.o.send(c.id, PING);
    }
  }

  private drop(conn: string) { this.conns.delete(conn); this.o.close(conn); }
}

// ---------- a phone ----------

export interface ClientOptions extends Me {
  send: (text: string) => void;
  close: () => void;
  onMessage: (msg: LanMessage) => void;
  onReady?: (door: string) => void;
}

export class LanClient {
  private key: CryptoKey | null = null;
  private door: string | null = null;
  /** When the door was last heard (any frame, pings included). */
  heard = Date.now();
  private o: ClientOptions;
  constructor(o: ClientOptions) { this.o = o; }
  get ready() { return this.key !== null; }

  async received(text: string) {
    let f: Record<string, string>;
    try { f = JSON.parse(text); } catch { return this.o.close(); }
    this.heard = Date.now();
    if (f.t === "p") return;
    if (!this.key) {
      // The door's hi: an approved door tablet of this school, signing a fresh nonce and key.
      if (f.t !== "hi" || f.site !== this.o.siteId) return this.o.close();
      const d = peer(this.o, f.door!, "kiosk");
      const ok = d && await verifyCanonical(d.pubkey, { p: PROTO, t: "hi", site: f.site, door: f.door, eph: f.eph, n: f.n }, f.sig!).catch(() => false);
      if (!ok) return this.o.close();
      const eph = await ephemeral();
      const sig = await signCanonical(this.o.privateKey, { p: PROTO, t: "hello", site: this.o.siteId, device: this.o.deviceId, eph: eph.pub, door_eph: f.eph, n: f.n });
      this.o.send(JSON.stringify({ t: "hello", device: this.o.deviceId, eph: eph.pub, sig }));
      this.key = await channelKey(eph.key, f.eph!, f.n!, f.eph!, eph.pub);
      this.door = f.door!;
      this.o.onReady?.(this.door);
      return;
    }
    if (f.t !== "x") return;
    let msg: LanMessage;
    try { msg = await open(this.key, f as { iv: string; ct: string }); } catch { return this.o.close(); }
    if (msg.k === "catchup") {
      const d = lanToday(msg);
      for (const ev of d.events) if (await eventIsGenuine(this.o, ev)) this.o.onMessage({ k: "event", ev });
      for (const a of d.answers) this.o.onMessage({ k: "answer", answer: a });
      return;
    }
    if (msg.k === "event" && (!fresh(msg.ev.pre?.device_ts) || !(await eventIsGenuine(this.o, msg.ev)))) return;
    if (msg.k === "answer" && !fresh(msg.answer?.at)) return;
    this.o.onMessage(msg);
  }

  /** Pings the door; true if the link has gone silent too long (the caller closes it). */
  tick(now = Date.now()) {
    if (now - this.heard > LAN_SILENT_MS) return true;
    if (this.key) this.o.send(PING);
    return false;
  }

  /** Sends a record (from this phone's outbox) or an answer to the door, and through it to the others. */
  async publish(msg: LanOutgoing) {
    if (!this.key) return false;
    this.o.send(await seal(this.key, msg));
    return true;
  }
}
